<script setup lang="ts">
import type { SearchCriterions } from '~/models/Search'
import type { GradeCode } from '~/models/Grade'
import type { SortField } from '~/models/Content/Sort'
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import { faXmark } from '@fortawesome/duotone-regular-svg-icons'
import { ageGroupMapping, gradeTypeMapping } from '~/utils/gradeSorter'
import Button from '~/components/ui/Button.vue'
import AuSkeleton from '~/components/ui/AuSkeleton.vue'
import SearchSorter from '~/components/search/SearchSorter.vue'

interface GroupedCriterion {
  type: string
  group: string
  values: string[]
}

interface SingleCriterion {
  type: string
  value: string
}

const props = withDefaults(defineProps<{
  criterions: SearchCriterions
  isLoading: boolean
  totalCount?: number
  page?: number
  totalPages?: number
  field?: SortField
  groupGradesByAge?: boolean
  groupGradesByGradeType?: boolean
}>(), {
  groupGradesByAge: false,
  groupGradesByGradeType: false
})

const emit = defineEmits(['remove-criterion', 'reset-criterions', 'set-sort-field'])

const { t } = useI18n()

const mappingFunction = computed(() => (props.groupGradesByGradeType ? gradeTypeMapping : ageGroupMapping))

const flattenedCriterions = computed(() => {
  if (!props.groupGradesByAge && !props.groupGradesByGradeType) {
    return Object.entries(props.criterions).flatMap(([type, values]) =>
      (values ?? []).map(value => ({ type, value }))
    )
  }

  const groupedCriterions: Record<string, { type: string, values: Set<string> }> = {}
  const nonGroupedCriterions: { type: string, value: string }[] = []

  Object.entries(props.criterions).forEach(([type, values]) => {
    (values ?? []).forEach((value) => {
      let isGrouped = false
      for (const [group, keys] of Object.entries(mappingFunction.value)) {
        if (keys.includes(value as GradeCode)) {
          if (!groupedCriterions[group]) {
            groupedCriterions[group] = { type, values: new Set<string>() }
          }
          groupedCriterions[group].values.add(value)
          isGrouped = true
          break
        }
      }
      if (!isGrouped) {
        nonGroupedCriterions.push({ type, value })
      }
    })
  })

  const groupedCriterionsArray: (GroupedCriterion | SingleCriterion)[] = []
  Object.entries(groupedCriterions).forEach(([group, { type, values }]) => {
    const allKeys = mappingFunction.value[group]
    const allActive = allKeys.every(key => props.criterions[type]?.includes(key))
    if (allActive) {
      groupedCriterionsArray.push({ type, group, values: Array.from(values) } as GroupedCriterion)
    } else {
      Array.from(values).forEach((value) => {
        groupedCriterionsArray.push({ type, value } as SingleCriterion)
      })
    }
  })

  return [...groupedCriterionsArray, ...nonGroupedCriterions]
})

const removeCriterion = (type: string, values: string[] | string) => {
  if (Array.isArray(values)) {
    values.forEach((value) => {
      emit('remove-criterion', type, value)
    })
  } else {
    emit('remove-criterion', type, values)
  }
}

const translateGroup = (group: string) => group
  ? (props.groupGradesByGradeType ? t(`search.filterBoxes.${group}`) : group)
  : ''

const isGroupedCriterion = (criterion: GroupedCriterion | SingleCriterion): criterion is GroupedCriterion => 'group' in criterion

const translateCriterion = (criterion: { type: string, value: string }) => {
  const translationMap: Record<string, string> = {
    grade: `metadata.grades.${criterion.value}`,
    subject: `metadata.subjects.${criterion.value}`,
    activity: `activities.${criterion.value}`,
    label: `labels.${criterion.value}`,
    access: `access.${criterion.value}`,
    checkbox: `search.checkboxes.${criterion.value}`,
    support: 'search.filterBoxes.support',
    genre: `genre.${criterion.value.toLowerCase()}`,
  }

  return Object.hasOwn(translationMap, criterion.type)
    ? t(translationMap[criterion.type], criterion.value)
    : criterion.value
}
</script>

<template>
  <div class="grid-in-active-filters relative flex flex-wrap items-center gap-4 self-start">
    <div v-if="!isLoading">
      <p
        v-if="totalCount"
        class="flex w-max flex-col text-gray-50"
      >
        <span
          class="text-lg font-bold"
          v-text="t('search.resultsCount', { count: totalCount })"
        />
        <span
          v-if="totalCount > 0"
          class="text-xs"
          v-text="t('search.currentPage', { page, totalPages: Number(totalPages) > 0 ? totalPages : page })"
        />
      </p>
    </div>
    <AuSkeleton
      v-else
      height="44px"
      width="88px"
      class="!rounded-none"
    />
    <ul
      v-if="flattenedCriterions.length"
      class="flex flex-wrap gap-2 pr-12"
    >
      <li
        v-for="criterion in flattenedCriterions"
        :key="isGroupedCriterion(criterion) ? criterion.group : criterion.value"
      >
        <Button
          size="sm"
          :aria-label="t('search.filters.removeFilter', { filter: isGroupedCriterion(criterion) ? translateGroup(criterion.group) : translateCriterion(criterion) })"
          @click="removeCriterion(criterion.type, isGroupedCriterion(criterion) ? criterion.values : criterion.value)"
        >
          <font-awesome-icon :icon="faXmark" />
          <span
            class="first-letter:capitalize"
            v-text="isGroupedCriterion(criterion) ? translateGroup(criterion.group) : translateCriterion(criterion)"
          />
        </Button>
      </li>
      <li v-if="flattenedCriterions.length > 1">
        <Button
          variant="outline"
          size="sm"
          :aria-label="t('search.filters.removeAllFilters')"
          @click="emit('reset-criterions')"
        >
          <font-awesome-icon :icon="faXmark" />
          {{ t('search.reset') }}
        </Button>
      </li>
    </ul>
    <div
      v-if="field"
      class="absolute top-1.5 right-0"
    >
      <SearchSorter
        :field="field"
        @set-sort-field="emit('set-sort-field', $event)"
      />
    </div>
  </div>
</template>
